<script setup lang="ts">
  import { ref, defineModel, Ref, onMounted, watch, defineEmits, onUnmounted, nextTick } from 'vue'
  import axios from 'axios';

  import MediaThumbnails from './MediaThumbnails.vue'
  import { Media, MediaCollection, ParentModel } from '@/types'
  import MediaForm from './MediaForm.vue';
  import MediaTable from './MediaTable.vue';

  const selectedMedia = defineModel('selectedMedia', {
    type: Number,
    default: null,
  })

  const props = defineProps({
    parent: {
      type: Object,
      default: null,
    }
  })

  const emit = defineEmits(['update'])

  const addMedia = (media: Media) => {
    medias.value.unshift(media)
  }

  defineExpose({
    addMedia,
  })

  const medias: Ref<Array<Media>> = ref([])
  const collections: Ref<Array<MediaCollection>> = ref([])
  const selectedView = ref<number>(1)
  const selectedCollection = ref<number|null>(null)
  const loading = ref<boolean>(false)
  const loadingMore = ref<boolean>(false)
  const search = ref<string>('')
  const parent = ref<ParentModel>(props.parent)
  const hasMore = ref<boolean>(false)
  const currentPage = ref<number>(1)

  const generalFilter = ref<string>('')
  const monthFilter = ref<number|null>(null)
  const yearFilter = ref<number|null>(null)

  const scrollContainer = ref<HTMLElement|null>(null)
  const sentinel = ref<HTMLElement|null>(null)
  let observer: IntersectionObserver|null = null
  let searchTimeout: ReturnType<typeof setTimeout>|null = null

  let filters = [
    {
      name: 'Alle',
      value: '',
    },
    {
      name: 'Nicht angehängt',
      value: 'not_attached',
    },
    {
      name: 'Selbst hochgeladen',
      value: 'self_uploaded',
    }
  ]

  const months = [
    { name: '', value: null },
    { name: 'Januar', value: 1 },
    { name: 'Februar', value: 2 },
    { name: 'März', value: 3 },
    { name: 'April', value: 4 },
    { name: 'Mai', value: 5 },
    { name: 'Juni', value: 6 },
    { name: 'Juli', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'Oktober', value: 10 },
    { name: 'November', value: 11 },
    { name: 'Dezember', value: 12 },
  ]

  const arrayRange = (start, stop, step) =>
    Array.from(
      { length: (stop - start) / step + 1 },
      (value, index) => start + index * step
    );

  const years = [ null, ...arrayRange(2017, new Date().getFullYear(), 1)]

  onMounted(async () => {
    if (parent.value) {
      filters.splice(1, 0, {
        name: `In diesem Beitrag hochgeladen`,
        value: 'uploaded_in_post',
      })
    }

    await fetchMedia()
    setupIntersectionObserver()
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
    }
  })

  const buildParams = (page: number = 1) => {
    const params: Record<string, any> = { page, per_page: 50 }

    if (search.value) params.search = search.value
    if (selectedCollection.value) params.collection = selectedCollection.value
    if (generalFilter.value) {
      params.filter = generalFilter.value
      if (generalFilter.value === 'uploaded_in_post' && parent.value) {
        params.parent_type = parent.value.type
        params.parent_id = parent.value.id
      }
    }
    if (monthFilter.value !== null) params.month = monthFilter.value
    if (yearFilter.value !== null) params.year = yearFilter.value

    return params
  }

  const fetchMedia = async (append: boolean = false) => {
    if (append) {
      loadingMore.value = true
    } else {
      loading.value = true
    }

    try {
      const page = append ? currentPage.value + 1 : 1
      const response = await axios.get('/api/media', { params: buildParams(page) })

      if (append) {
        medias.value.push(...response.data.media)
      } else {
        medias.value = response.data.media
      }

      collections.value = response.data.media_collections
      hasMore.value = response.data.has_more
      currentPage.value = response.data.current_page
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  const loadMore = async () => {
    if (loadingMore.value || !hasMore.value) return
    await fetchMedia(true)
  }

  const resetAndFetch = async () => {
    selectedMedia.value = null
    await fetchMedia(false)
    await nextTick()
    setupIntersectionObserver()
  }

  const setupIntersectionObserver = () => {
    if (observer) {
      observer.disconnect()
    }

    if (!sentinel.value || !scrollContainer.value) return

    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore.value && !loadingMore.value) {
          loadMore()
        }
      },
      { root: scrollContainer.value, rootMargin: '200px' }
    )

    observer.observe(sentinel.value)
  }

  // Debounced search
  watch(search, () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      resetAndFetch()
    }, 300)
  })

  // Immediate filter changes
  watch([generalFilter, monthFilter, yearFilter, selectedCollection], () => {
    resetAndFetch()
  })

  const selectCollection = (collectionId: number|null = null) => {
    if (collectionId === selectedCollection.value) {
      selectedCollection.value = null
    } else {
      selectedCollection.value = collectionId
    }
  }

  const updateMedia = (response) => {
    const index = medias.value.findIndex((media) => media.id === response.media.id)

    if (index > -1) {
      medias.value.splice(index, 1, response.media)
    } else {
      medias.value.unshift(response)
    }

    emit('update', response)
  }

  const removeMedia = (mediaId) => {
    const index = medias.value.findIndex((item) => item.id === mediaId)

    if (index > -1) {
      medias.value.splice(index, 1)
    }
  }
</script>

<template>
  <div class="flex flex-col-reverse lg:flex-row">
    <div class="lg:w-9/12 w-full" :class="{ 'lg:w-full': selectedMedia === null}">
      <div class="card w-full bg-base-300 min-h-[400px] relative">
        <div class="card-body overflow-hidden">
          <div class="flex items-center justify-between mb-2 flex-cols md:flex-row flex-wrap md:flex-nowrap">
            <div class="flex flex-wrap lg:flex-nowrap w-full">
              <div class="flex mb-2 w-full lg:w-4/6">
                <label class="input input-bordered flex items-center gap-2 mr-2 w-4/6">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-4 h-4 opacity-70"><path fill-rule="evenodd" d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z" clip-rule="evenodd" /></svg>
                  <input type="text" placeholder="Suche" v-model="search"/>
                </label>
                <select class="select select-bordered mr-2 w-2/6" v-model="generalFilter">
                  <option v-for="(filter, index) in filters" :key="index" :value="filter.value">{{ filter.name }}</option>
                </select>
              </div>
              <div class="flex w-full lg:w-2/6">
                <select class="select select-bordered mr-2 w-full" v-model="monthFilter" placeholder="Nach Monat">
                  <option disabled>Nach Monat</option>
                  <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.name }}</option>
                </select>
                <select class="select select-bordered mr-2 w-full" v-model="yearFilter" placeholder="Nach Jahr">
                  <option disabled>Nach Jahr</option>
                  <option v-for="(year, index) in years" :key="index" :value="year">{{ year }}</option>
                </select>
              </div>
            </div>
            <ul class="menu menu-horizontal bg-base-200 rounded-box md:ml-2 md:mt-0 mt-2 min-w-[120px]">
              <li>
                <button @click.prevent="() => selectedView = 1" :class="{ 'active fill-white': selectedView === 1 }">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24"><title>view-grid-outline</title><path d="M3 11H11V3H3M5 5H9V9H5M13 21H21V13H13M15 15H19V19H15M3 21H11V13H3M5 15H9V19H5M13 3V11H21V3M19 9H15V5H19Z" /></svg>
                </button>
              </li>
              <li>
                <button @click.prevent="() => selectedView = 2" :class="{ 'active fill-white': selectedView === 2 }">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24"><title>view-list</title><path d="M9,5V9H21V5M9,19H21V15H9M9,14H21V10H9M4,9H8V5H4M4,19H8V15H4M4,14H8V10H4V14Z" /></svg>
                </button>
              </li>
            </ul>
          </div>
          <div ref="scrollContainer" class="max-h-[550px] overflow-y-auto">
            <div class="flex flex-wrap items-center">
              <div class="m-1">
                <button class="btn" :class="{ 'btn-info': selectedCollection === null}" @click.prevent="selectCollection(null)">Alle</button>
              </div>
              <div class="m-1" v-for="(collection, index) in collections" :key="index">
                <button class="btn" :class="{ 'btn-info': selectedCollection === collection.id }" @click.prevent="selectCollection(collection.id)">
                  {{ collection.name }}
                </button>
              </div>
            </div>
            <MediaThumbnails v-if="selectedView === 1" v-model:selectedMedia="selectedMedia" :medias="medias"/>
            <MediaTable v-if="selectedView === 2" v-model:selectedMedia="selectedMedia" :medias="medias"/>
            <!-- Sentinel for infinite scroll -->
            <div ref="sentinel" class="h-4"></div>
            <div v-if="loadingMore" class="flex justify-center py-4">
              <span class="loading loading-spinner loading-md"></span>
            </div>
            <div v-if="!hasMore && medias.length > 0" class="text-center text-sm text-gray-400 py-2">
              Keine weiteren Medien
            </div>
          </div>
          <div v-if="loading" class="absolute w-full h-full flex items-center justify-center top-0 left-0">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full lg:w-3/12 mb-4 lg:mb-0" :class="{ 'lg:hidden': selectedMedia === null }">
      <MediaForm v-if="selectedMedia" :media="medias.find((media) => media.id === selectedMedia)" :collections="collections" :parent="parent" @updated="updateMedia" @deleted="removeMedia"/>
    </div>
  </div>
</template>
